import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { SortBarOption, SortBarOrder, SortOrder } from './sort-bar.option.enum';

@Component({
  selector: 'fgbcl-sort-bar',
  templateUrl: './sort-bar.component.html',
  styleUrls: ['./sort-bar.component.scss'],
})
export class SortBarComponent implements OnInit {
  /** Options to set up the properties to sort by and their labels. */
  @Input() sortBarOptions: SortBarOption[];
  /** Reference to the list that needs to be sorted. */
  @Input() itemList: any[];
  /** Adds a _Default_ sorting option to the sorting menu items. */
  @Input() displayDefaultSort: boolean = false;
  /** Event emitted when the _Default_ menu item has been selected. */
  @Output() defaultSortAction: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() sortChange: EventEmitter<{ property: string; order: SortBarOrder } | null> = new EventEmitter<{
    property: string;
    order: SortBarOrder;
  } | null>();
  /** Select the dropdown from the template */
  @ViewChild(NgbDropdown) dropdown: NgbDropdown;

  sortOrderType = SortOrder;
  optionSelected: string | undefined;

  constructor() {}

  ngOnInit() {}

  /**
   * Emits _defaultSortAction_.
   * This allows the parent component to reapply the default order, or to perform a more complex sorting algorithm.
   * Default button is not displayed unless _displayDefaultSort_ is set to true.
   * Dropdown needs to be closed manually to prevent fingertap glitches triggered by NgbDropdown autoClose.
   */
  sortByDefault() {
    this.optionSelected = '';
    this.defaultSortAction.emit();
    this.sortChange.emit(null);

    this.dropdown.close();
  }

  /**
   * Orders the itemList from the parent component based on the user selection.
   * @param selectedOption sorting option selected by the user, amongst the sortBarOptions set up from the parent component.
   * @param newOrder order selected by the user, Ascending or Descending.
   * Dropdown needs to be closed manually to prevent fingertap glitches triggered by NgbDropdown autoClose.
   */
  sortByProperty(selectedOption: SortBarOption, newOrder: SortOrder) {
    selectedOption.order = newOrder;
    this.resetOptionSelected(selectedOption);
    this.toggleOrderSelection(selectedOption);

    if (this.itemList) {
      this.orderByProperty(selectedOption);
    }
    const order: SortBarOrder = newOrder === SortOrder.Ascending ? 'asc' : 'desc';
    this.sortChange.emit({ property: selectedOption.propertyToSort, order });
    this.dropdown.close();
  }

  /**
   * Sets the order (_Ascending_/_Descending_) of the option according to the user's selection.
   * @param selectedOption sorting option selected by the user, amongst the sortBarOptions set up from the parent component.
   */
  private toggleOrderSelection(selectedOption: SortBarOption) {
    const x = this.sortBarOptions.find((option) => option === selectedOption);
    if (x) {
      x.order = selectedOption.order;
    }
  }

  /**
   * Sorts the itemList from the parent component by the property set up in sortBarOptions.
   * @param selectedOption sorting option selected by the user, amongst the sortBarOptions set up from the parent component.
   */
  private orderByProperty(selectedOption: SortBarOption) {
    this.itemList.sort((a, b) => {
      const x = (a as any)[selectedOption.propertyToSort];
      const y = (b as any)[selectedOption.propertyToSort];

      const comparison = isNaN(x) ? x.localeCompare(y) : x - y;
      return selectedOption.order * comparison;
    });
  }

  /**
   * Sets the _optionSelected_ property to the label set up in sortBarOptions.
   * @param selectedOption sorting option selected by the user, amongst the sortBarOptions set up from the parent component.
   * @param newOrder order selected by the user, Ascending or Descending.
   */
  private resetOptionSelected(selectedOption: SortBarOption) {
    switch (selectedOption.order) {
      case SortOrder.Ascending:
        this.optionSelected = selectedOption.labelASC;
        break;
      case SortOrder.Descending:
        this.optionSelected = selectedOption.labelDESC;
        break;
    }
  }
}
